/* zapiet-quantity-guard.js (patched)
   Force-cart-minimum: force qty -> min when user reduces; disable checkout until all restricted items meet min
   - Reads restaurant rules from window.QTY_LIMITER_RESTAURANTS (fallback to window.DATE_BASED_QTY_LIMITS)
   - Honors window.RESTRICTED_DATES if provided (if empty -> always enforce)
   - Optimized: caches cart per validation run to avoid repeated fetches
*/

(function () {
  const GLOBAL_MAP = window.DATE_BASED_QTY_LIMITS || {};
  const RESTAURANT_RULES = window.QTY_LIMITER_RESTAURANTS || {};
  const RESTRICTED_DATES = Array.isArray(window.RESTRICTED_DATES) ? window.RESTRICTED_DATES : [];
  const DEBUG = !!window.__QTY_LIMITER_DEBUG;

  const toInt = v => {
    const n = parseInt(String(v || "").replace(/[^\d]/g, ""), 10);
    return Number.isNaN(n) ? 0 : n;
  };

  async function fetchCart() {
    try { return await fetch("/cart.js", { cache: "no-store" }).then(r => r.json()); }
    catch { return null; }
  }

  function log(...args) { if (DEBUG) console.log("[qty-guard]", ...args); }

  // Extract the base variant id from inputs like:
  function extractVariantIdFromInput(inp) {
    if (!inp) return null;
    const ds = inp.dataset || {};
    const tryKeys = [ds.variant, ds.variantId, inp.getAttribute("data-variant"), inp.getAttribute("data-id"), inp.id, inp.name, inp.value];
    for (const v of tryKeys) {
      if (!v) continue;
      const m = String(v).match(/(\d{5,})/); // match digits
      if (m) return String(m[1]);
    }
    return null;
  }

  // Determine min for a variant (restaurant-aware + date-aware)
  // Pass cart in to avoid repeated fetches
  async function getMinForVariant(variantId, cart) {
    if (!variantId) return null;
    if (!cart) return null;

    const zapDate = cart.attributes?.["Delivery-Date"] || null;
    if (RESTRICTED_DATES.length > 0 && (!zapDate || !RESTRICTED_DATES.includes(zapDate))) {
      log("date not active", variantId, zapDate);
      return null; // not an active date
    }

    // Prefer restaurant-specific rule stored in cart attributes
    const rest = cart.attributes?.["Restaurant-Id"] || null;

    if (rest && RESTAURANT_RULES && RESTAURANT_RULES[rest] && RESTAURANT_RULES[rest][variantId]) {
      const v = Number(RESTAURANT_RULES[rest][variantId]);
      return Number.isFinite(v) ? v : null;
    }

    if (GLOBAL_MAP && GLOBAL_MAP[variantId]) {
      const v = Number(GLOBAL_MAP[variantId]);
      return Number.isFinite(v) ? v : null;
    }

    return null;
  }

  // UI helpers
  function showInline(inp, text) {
    if (!inp || !inp.parentNode) return;
    let box = inp.parentNode.querySelector(".qty-force-inline");
    if (!box) {
      box = document.createElement("div");
      box.className = "qty-force-inline";
      box.style.color = "#b00020";
      box.style.fontSize = "13px";
      box.style.marginTop = "6px";
      inp.parentNode.appendChild(box);
    }
    box.innerHTML = text;
    box.style.display = "block";
  }
  function hideInline(inp) {
    if (!inp || !inp.parentNode) return;
    const box = inp.parentNode.querySelector(".qty-force-inline");
    if (box) box.style.display = "none";
  }

  function disableCheckout(form) {
    if (!form) return;
    const ck = form.querySelector("button[name='checkout'], .t4s-btn__checkout, .t4s-cartPage__btn-checkout, a[href*='/checkout']");
    if (ck) {
      try { ck.disabled = true; } catch(e){}
      ck.classList.add("disabled-hard");
      ck.style.pointerEvents = "none";
      ck.style.opacity = "0.5";
    }
  }
  function enableCheckout(form) {
    if (!form) return;
    const ck = form.querySelector("button[name='checkout'], .t4s-btn__checkout, .t4s-cartPage__btn-checkout, a[href*='/checkout']");
    if (ck) {
      try { ck.disabled = false; } catch(e){}
      ck.classList.remove("disabled-hard");
      ck.style.pointerEvents = "";
      ck.style.opacity = "";
    }
  }

  // Validate all restricted inputs inside form: return true if any violation exists
  async function validateForm(cartForm) {
    if (!cartForm) return false;
    const inputs = Array.from(cartForm.querySelectorAll(".t4s-quantity-input[name='updates[]']"));
    if (!inputs.length) return false;

    const cart = await fetchCart();
    if (!cart) return false;

    let anyViolation = false;
    // We'll dispatch change events in batch after forced adjustments
    const toDispatch = [];

    for (const inp of inputs) {
      const vid = extractVariantIdFromInput(inp);
      const min = await getMinForVariant(vid, cart);
      if (!min) {
        hideInline(inp);
        continue;
      }
      try { inp.min = String(min); } catch (e) {}
      const val = toInt(inp.value);
      if (val < min) {
        anyViolation = true;
        showInline(inp, `Minimum <b>${min}</b> pcs required — quantity set to minimum.`);
        inp.value = String(min);
        toDispatch.push(inp);
      } else {
        hideInline(inp);
      }
    }

    // Dispatch change events after loop so theme can react once
    for (const inp of toDispatch) {
      try { inp.dispatchEvent(new Event("change", { bubbles: true })); } catch(e){}
    }

    if (anyViolation) disableCheckout(cartForm);
    else enableCheckout(cartForm);

    log("validateForm done", { anyViolation });
    return anyViolation;
  }

  // Force clamp one input if user attempts below min (immediate)
  async function enforceOnInput(inp) {
    if (!inp) return;
    const cart = await fetchCart();
    if (!cart) return;
    const vid = extractVariantIdFromInput(inp);
    const min = await getMinForVariant(vid, cart);
    if (!min) return;
    const val = toInt(inp.value);
    if (val < min) {
      inp.value = String(min);
      showInline(inp, `Adjusted to minimum <b>${min}</b> pcs`);
      setTimeout(() => hideInline(inp), 1600);
      try { inp.dispatchEvent(new Event("change", { bubbles: true })); } catch(e){}
      return true;
    } else {
      hideInline(inp);
      return false;
    }
  }

  // Handle decrease click: if result would be < min, force min and block default effect
  async function handleDecrease(buttonEl) {
    if (!buttonEl) return false;
    const wrapper = buttonEl.closest("[data-quantity-wrapper], .t4s-quantity-wrapper, .t4s-quantity-cart-item");
    const inp = wrapper ? wrapper.querySelector(".t4s-quantity-input[name='updates[]']") : null;
    if (!inp) return false;
    const cart = await fetchCart();
    const vid = extractVariantIdFromInput(inp);
    const min = await getMinForVariant(vid, cart);
    if (!min) return false;

    const current = toInt(inp.value);
    const next = current - 1;
    if (next < min) {
      inp.value = String(min);
      showInline(inp, `Minimum <b>${min}</b> quantities required for this date.`);
      setTimeout(()=>hideInline(inp),1300);
      try { inp.dispatchEvent(new Event("change",{bubbles:true})); } catch(e){}
      return true; // blocked
    }
    return false;
  }

  // Attach listeners to the cart form
  function attachForm(cartForm) {
    if (!cartForm || cartForm.__qtyForceAttached) return;
    cartForm.__qtyForceAttached = true;

    cartForm.addEventListener("input", (e) => {
      const t = e.target;
      if (!t) return;
      if (t.matches && t.matches(".t4s-quantity-input[name='updates[]']")) {
        enforceOnInput(t).then(()=> validateForm(cartForm));
      }
    }, true);

    cartForm.addEventListener("blur", (e) => {
      const t = e.target;
      if (!t) return;
      if (t.matches && t.matches(".t4s-quantity-input[name='updates[]']")) {
        enforceOnInput(t).then(()=> validateForm(cartForm));
      }
    }, true);

    cartForm.addEventListener("paste", (e) => {
      const t = e.target;
      if (!t) return;
      if (t.matches && t.matches(".t4s-quantity-input[name='updates[]']")) {
        setTimeout(()=> enforceOnInput(t).then(()=> validateForm(cartForm)), 10);
      }
    }, true);

    cartForm.addEventListener("click", async (e) => {
      const dec = e.target.closest && e.target.closest("[data-decrease-qty], [data-quantity-selector].is--minus, .t4s-quantity-selector.is--minus");
      const inc = e.target.closest && e.target.closest("[data-increase-qty], [data-quantity-selector].is--plus, .t4s-quantity-selector.is--plus");
      if (dec) {
        const blocked = await handleDecrease(dec);
        if (blocked) { e.preventDefault(); e.stopImmediatePropagation(); return false; }
        setTimeout(()=> validateForm(cartForm), 60);
      } else if (inc) {
        setTimeout(()=> validateForm(cartForm), 60);
      }
    }, true);

    cartForm.addEventListener("click", async (e) => {
      const ck = e.target.closest && e.target.closest("button[name='checkout'], .t4s-btn__checkout, .t4s-cartPage__btn-checkout, a[href*='/checkout']");
      if (!ck) return;
      const violation = await validateForm(cartForm);
      if (violation) {
        e.preventDefault();
        e.stopImmediatePropagation();
        return false;
      }
    }, true);

    cartForm.addEventListener("submit", async (e) => {
      const violation = await validateForm(cartForm);
      if (violation) {
        e.preventDefault();
        e.stopImmediatePropagation();
        return false;
      }
    }, true);

    setTimeout(()=> validateForm(cartForm), 300);
  }

  function start() {
    const form = document.querySelector('form[action="/cart"]');
    if (form) attachForm(form);
  }

  start();
  new MutationObserver(start).observe(document.body, { childList: true, subtree: true });

  // Expose helpers
  window.__qtyLimiterCartForce = window.__qtyLimiterCartForce || {};
  window.__qtyLimiterCartForce.validateNow = async () => {
    const f = document.querySelector('form[action="/cart"]');
    return await validateForm(f);
  };
  window.__qtyLimiterCartForce.forceAllToMin = async () => {
    const f = document.querySelector('form[action="/cart"]');
    if(!f) return false;
    const inputs = Array.from(f.querySelectorAll(".t4s-quantity-input[name='updates[]']"));
    const cart = await fetchCart();
    for(const inp of inputs){
      const vid = extractVariantIdFromInput(inp);
      const m = await getMinForVariant(vid, cart);
      if(m && toInt(inp.value) < m){
        inp.value = String(m);
        try { inp.dispatchEvent(new Event("change",{bubbles:true})); } catch(e){}
      }
    }
    return await validateForm(f);
  };

})();
